import * as React from 'react';

import { FieldValues, Path, useController, useFormContext } from 'react-hook-form';
import { Menu as ChakraCheckBoxMenu, Button, Portal, Box, useCheckboxGroup } from '@chakra-ui/react';
import {
	Field,
	CheckBoxMenuContent,
	CheckBoxMenuItem,
	CheckBoxMenuItemGroup,
	CheckBoxMenuItemGroupLabel,
	CheckBoxMenuItemIndicator,
	CheckBoxMenuPositioner,
	CheckBoxMenuRoot,
	CheckBoxMenuTrigger,
	FieldProps,
} from '../chakra-components';
import Res from 'core/api/Res';


export interface CheckBoxMenuItemDef {
	value: string;
	linkTo?: string;
	title: React.ReactNode;
	disabled?: boolean;
	checked: boolean;
	onCheckedChange: (checked: boolean) => void;
}


export interface CheckBoxMenuProps extends Omit<ChakraCheckBoxMenu.RootProps, 'value' | 'onChange' | 'checked' | 'defaultChecked' | 'onCheckedChange' | 'children'> {
	ref?: React.Ref<HTMLDivElement>;
	label?: React.ReactNode;
	triggerLabel?: React.ReactNode;
	items: CheckBoxMenuItemDef[];
	fieldProps?: FieldProps;
	icon?: React.ReactNode;
}


export type CheckBoxMenuRef = HTMLDivElement;

export const CheckBoxMenu = (props: CheckBoxMenuProps & { ref?: React.Ref<CheckBoxMenuRef> }) => {
	const { ref, fieldProps, label, triggerLabel, items, icon, ...rootProps } = props;

	const itemsRef = React.useRef(items);
	itemsRef.current = items;

	const defaultValue = React.useMemo(
		() => items.filter(i => i.checked).map(i => i.value),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	);

	const group = useCheckboxGroup({
		defaultValue,
		onValueChange: (nextValue) => {
			for (const item of itemsRef.current) {
				const nowChecked = nextValue.includes(item.value);
				if (nowChecked !== item.checked)
					item.onCheckedChange(nowChecked);
			}
		},
	});

	return (
		<Field {...fieldProps}>
			<Box ref={ref} width={'full'}>
				{items.filter(item => item.linkTo !== undefined).map(item => (
					<FormFieldSync key={item.value} linkTo={item.linkTo!} checked={item.checked} />
				))}
				<CheckBoxMenuRoot {...rootProps} closeOnSelect={false}>
					<CheckBoxMenuTrigger asChild>
						<Button variant={'outline'} width={'full'} as={'div'} justifyContent={'space-between'}>
							{triggerLabel ?? label ?? Res.Common.CHECK_BOX_MENU_PLACEHOLDER_LBL()}{icon ? icon : null}
						</Button>
					</CheckBoxMenuTrigger>
					<Portal disabled={true}>
						<CheckBoxMenuPositioner>
							<CheckBoxMenuContent>
								<CheckBoxMenuItemGroup>
									{label && <CheckBoxMenuItemGroupLabel>{label ?? Res.Common.CHECK_BOX_MENU_PLACEHOLDER_LBL()}</CheckBoxMenuItemGroupLabel>}
									{items.map(({ value, title, disabled }) => (
										<CheckBoxMenuItem
											key={value}
											value={value}
											disabled={disabled}
											closeOnSelect={false}
											checked={group.isChecked(value)}
											onCheckedChange={() => group.toggleValue(value)}>
											{title}
											<CheckBoxMenuItemIndicator />
										</CheckBoxMenuItem>
									))}
								</CheckBoxMenuItemGroup>
							</CheckBoxMenuContent>
						</CheckBoxMenuPositioner>
					</Portal>
				</CheckBoxMenuRoot>
			</Box>
		</Field >
	);
};


// Sincroniza el estado del check con el campo correspondiente del formulario (react-hook-form),
// para que formRef.current.getValue() devuelva siempre el valor actual del check.
function FormFieldSync<T extends FieldValues>({ linkTo, checked }: { linkTo: string; checked: boolean }) {

	const formContext = useFormContext<T>();
	const { field } = useController<T>({ name: linkTo as Path<T>, control: formContext?.control });

	React.useEffect(() => {
		if (field.value !== checked)
			field.onChange(checked);
	}, [checked, field]);

	return null;
}